import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Building2, FileSpreadsheet, LogOut, Truck, HardHat, UserCog, Wrench, PhoneCall, Mail, Receipt, Wallet, Boxes, BookOpen, BookMarked, Trash2, ClipboardCheck, ShieldCheck, Calendar as CalIcon, CheckSquare, Plug, CalendarClock, Smartphone, HelpCircle, FileText, Sunrise, Package, Calculator as CalcIcon, FolderKanban, TrendingUp, Wallet as FinanceIcon, Settings, Search, ClipboardList, UserRoundCog, Map as MapIcon, DollarSign, Plus, Globe, Hammer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GetAppOnPhoneModal from "@/components/GetAppOnPhoneModal";
import GlobalSearch from "@/components/GlobalSearch";
import NewLeadWizard from "@/components/NewLeadWizard";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useForceSpellcheck } from "@/hooks/useForceSpellcheck";

/**
 * Grouped sidebar navigation.
 *
 * Each top-level group has a lightweight header + list of nav items.
 * `adminOnly: true` items get hidden for non-admin users; a group whose
 * ALL items are admin-only will disappear entirely when a non-admin
 * signs in (see filter logic in Layout()).
 *
 * Order matches the field team's day: Dashboard → who we serve →
 * what we build → daily field ops → weekly reports → time & docs →
 * money → back-office. User Guide sits at the very bottom, separated,
 * as an external link.
 */
const NAV_GROUPS = [
  {
    label: null,  // "Dashboard" stands alone at the top with no group header
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    ],
  },
  {
    label: "Contacts",
    icon: Users,
    items: [
      { to: "/contacts",       label: "People & Companies", icon: Users,        testId: "nav-contacts" },
      { to: "/vendors",        label: "Vendors",            icon: Truck,        testId: "nav-vendors" },
      { to: "/subcontractors", label: "Subcontractors",     icon: HardHat,      testId: "nav-subcontractors", nonSales: true },
      { to: "/employees",      label: "Employees",          icon: UserRoundCog, testId: "nav-employees", adminOnly: true },
      { to: "/users",          label: "Users",              icon: UserCog,      testId: "nav-users", adminOnly: true },
    ],
  },
  {
    label: "Projects",
    icon: FolderKanban,
    items: [
      { to: "/properties", label: "Properties", icon: Building2,       testId: "nav-properties" },
      { to: "/projects",   label: "Deals",      icon: FileSpreadsheet, testId: "nav-projects" },
      { to: "/calculator", label: "Build Scope", icon: CalcIcon,        testId: "nav-calculator" },
      { to: "/single-ply", label: "Traditional Est.", icon: Boxes,      testId: "nav-single-ply" },
      { to: "/custom-estimates", label: "Custom Estimates", icon: Hammer, testId: "nav-custom-estimates" },
    ],
  },
  {
    label: "Reports",
    icon: TrendingUp,
    items: [
      { to: "/assessments", label: "Assessments", icon: ClipboardCheck, testId: "nav-assessments" },
      { to: "/scopes",      label: "Scopes",      icon: FileText,       testId: "nav-scopes" },
      { to: "/maintenance", label: "Maintenance", icon: Wrench,         testId: "nav-maintenance" },
      { to: "/service",     label: "Service Calls", icon: PhoneCall,    testId: "nav-service" },
      { to: "/reports/job-costs", label: "Job Costs", icon: DollarSign, testId: "nav-job-costs", managerOrAdmin: true },
      { to: "/reports/win-loss", label: "Win / Loss", icon: TrendingUp, testId: "nav-win-loss", managerOrAdmin: true },
      { to: "/reports/warranties", label: "Warranties", icon: ShieldCheck, testId: "nav-warranties" },
      { to: "/reports/ar-aging", label: "A/R Aging", icon: Wallet, testId: "nav-ar-aging", managerOrAdmin: true },
    ],
  },
  {
    label: "Scheduling",
    icon: CalendarClock,
    items: [
      { to: "/calendar",          label: "Calendar",       icon: CalIcon,       testId: "nav-calendar" },
      { to: "/crew-schedule",     label: "Crew Schedule",  icon: HardHat,       testId: "nav-crew-schedule" },
      { to: "/tasks",             label: "Tasks",          icon: CheckSquare,   testId: "nav-tasks" },
      { to: "/settings/schedule", label: "Scheduled Jobs", icon: CalendarClock, testId: "nav-schedule", adminOnly: true },
      { to: "/time", label: "Time & Expenses", icon: CalendarClock, testId: "nav-time", managerOrAdmin: true },
      { to: "/sub-attendance", label: "Sub Attendance", icon: HardHat, testId: "nav-sub-attendance", managerOrAdmin: true },
    ],
  },
  {
    label: "Library",
    icon: BookOpen,
    items: [
      { to: "/library",   label: "Documents",         icon: BookOpen, testId: "nav-library" },
      { to: "/catalog",   label: "Product Materials", icon: Package,  testId: "nav-catalog", nonSales: true },
    ],
  },
  {
    label: "Finance",
    icon: FinanceIcon,
    items: [
      { to: "/books",    label: "Books",    icon: BookMarked, testId: "nav-books" },
      { to: "/invoices", label: "Invoices", icon: Receipt,    testId: "nav-invoices" },
      { to: "/commissions/mine", label: "My Commissions", icon: DollarSign, testId: "nav-my-commissions" },
      { to: "/commissions", label: "Commissions", icon: DollarSign, testId: "nav-commissions-admin", adminOnly: true },
      { to: "/payables", label: "Payables", icon: Wallet,     testId: "nav-payables" },
    ],
  },
  {
    label: "Company Info",
    icon: Settings,
    items: [
      { to: "/settings/company-info", label: "Company Info",    icon: Building2, testId: "nav-company-info",   adminOnly: true },
      { to: "/settings/integrations",label: "Integrations",    icon: Plug,    testId: "nav-integrations",      adminOnly: true },
      { to: "/settings/website",     label: "Website Leads",   icon: Globe,   testId: "nav-website-leads",     adminOnly: true },
      { to: "/settings/equipment-rates", label: "Equipment Rates", icon: Truck, testId: "nav-equipment-rates", adminOnly: true },
      { to: "/settings/scope-bullets", label: "Scope Bullets",    icon: ClipboardList, testId: "nav-scope-bullets",      adminOnly: true },
      { to: "/settings/service-templates", label: "Service Templates", icon: Mail, testId: "nav-service-templates", adminOnly: true },
      { to: "/admin/system-map",     label: "System Map",         icon: MapIcon,      testId: "nav-system-map",         adminOnly: true },
      { to: "/trash",                label: "Trash",           icon: Trash2,  testId: "nav-trash",             adminOnly: true },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  // Force browser spellcheck ON for every input & textarea app-wide. Guards
  // against typos on the Assessment form, deal notes, invoice descriptions,
  // etc. Especially useful inside the iOS Capacitor WebView where spellcheck
  // is disabled by default. See useForceSpellcheck for details.
  useForceSpellcheck();
  // Filter out admin-only items for non-admins, then drop groups that
  // end up with zero visible items (e.g. Company Info for regular users).
  const isAdmin = user?.role === "admin";
  const isSales = user?.role === "sales";
  const GROUPS = NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => {
        if (i.adminOnly && !isAdmin) return false;
        if (i.managerOrAdmin && !isAdmin && user?.role !== "manager") return false;
        if (i.nonSales && isSales) return false;
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);
  const [showGetApp, setShowGetApp] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  // Reset scroll position of the main content pane on every route change.
  // React Router doesn't do this by default when scroll lives on a nested
  // container (our `<main>` scrolls, not `window`), so the previous page's
  // scroll bled into the next page and users landed at the bottom of every
  // navigation. Also resets the browser's own scroll for safety on mobile
  // (Capacitor / iOS PWA sometimes scrolls the window instead of main).
  const mainRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [location.pathname]);

  // Apply a body-level role class so a single stylesheet rule can hide
  // action affordances (delete buttons, financial columns, etc.) for
  // sales reps without touching every individual component. Backend still
  // enforces the same rules — the class is UX, not security.
  useEffect(() => {
    const cls = `role-${user?.role || "guest"}`;
    document.body.classList.add(cls);
    return () => document.body.classList.remove(cls);
  }, [user?.role]);
  return (
    <div className="min-h-screen flex bg-zinc-100">
      {/* Sidebar — hidden on phone-sized viewports. The phone app is
          strictly field-only (camera + video + note) via `/field`. */}
      <aside className="hidden md:flex w-64 bg-zinc-950 text-zinc-50 flex-col" data-testid="sidebar">
        <div className="p-4 bg-white border-b border-zinc-800">
          <img src="/sealtech-logo.png" alt="SealTech Building Solutions" className="w-full h-auto max-h-16 object-contain" />
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {user?.role !== "field_sub" && (
            <button
              onClick={() => setNewLeadOpen(true)}
              data-testid="new-lead-button"
              className="w-full mb-4 h-10 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Lead
            </button>
          )}
          {GROUPS.map((group, gi) => (
            <div key={group.label || `g${gi}`} className={gi === 0 ? "" : "mt-4"}>
              {group.label && (
                <div className="px-3 pb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                  {group.icon && <group.icon className="w-3 h-3" />}
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                        isActive
                          ? "bg-blue-700 text-white"
                          : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                      }`
                    }
                  >
                    <item.icon className="w-3.5 h-3.5" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
          {/* Training Guides — cheat sheets + full manual at /training. */}
          <NavLink
            to="/training"
            data-testid="nav-training-guides"
            className={({ isActive }) =>
              `mt-4 pt-3 border-t border-zinc-800/60 flex items-center gap-2.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                isActive ? "text-white bg-zinc-900" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
              }`
            }
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Training Guides
          </NavLink>
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <NavLink
            to="/profile"
            data-testid="nav-profile"
            className={({ isActive }) =>
              `flex items-center gap-3 mb-3 p-2 -m-2 rounded-sm transition-colors ${
                isActive ? "bg-blue-700/20" : "hover:bg-zinc-900"
              }`
            }
            title="Edit my profile"
          >
            <div className="w-9 h-9 rounded-sm bg-blue-700 flex items-center justify-center text-white font-heading font-black">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold truncate text-white" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{user?.role || "user"}{user?.title ? " · " + user.title : ""}</div>
            </div>
          </NavLink>
          <button
            data-testid="get-app-button"
            onClick={() => setShowGetApp(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 border border-blue-700 text-blue-400 hover:bg-blue-700 hover:text-white text-xs uppercase tracking-wider font-bold transition-colors rounded-sm"
          >
            <Smartphone className="w-3.5 h-3.5" />
            Get App on My Phone
          </button>

          {/* Printable how-to PDFs — regenerated live from the backend on every click. */}
          <button
            data-testid="dl-daily-status"
            onClick={async () => {
              const id = toast.loading("Building today's status report…");
              try {
                const r = await api.get("/reports/daily-status.pdf", { responseType: "blob" });
                const u = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                const date = new Date().toISOString().slice(0, 10);
                a.href = u; a.download = `Daily Status - ${date}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(u), 1000);
                toast.success("Daily Status downloaded", { id });
              } catch (e) {
                toast.error(e?.message || "Could not download", { id });
              }
            }}
            title="Today's pipeline snapshot — where every deal is, what's next, who owns it. Also auto-emails every weekday 7am MT."
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 hover:border-amber-500 text-xs uppercase tracking-wider font-bold transition-colors rounded-sm"
          >
            <Sunrise className="w-3.5 h-3.5" />
            Today&apos;s Status Report
          </button>

          <NavLink
            to="/training"
            data-testid="footer-training-guides"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-300 text-zinc-400 text-[10px] uppercase tracking-wider font-bold transition-colors rounded-sm"
            title="Cheat sheets + the full manual — Quick Guide, Crew Time & Attendance, and more"
          >
            <FileText className="w-3 h-3" />
            Training Guides
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-zinc-800 hover:border-blue-700 hover:text-blue-500 text-xs uppercase tracking-wider font-bold transition-colors rounded-sm"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main ref={mainRef} className="flex-1 overflow-auto">
        <Outlet />
      </main>

      {/* Floating Cmd+K trigger — bottom-right, always visible */}
      <button
        type="button"
        onClick={() => {
          const evt = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
          document.dispatchEvent(evt);
        }}
        title="Search everything (⌘K)"
        data-testid="global-search-trigger"
        className="fixed bottom-5 right-5 z-40 h-11 pl-3 pr-4 bg-zinc-950 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden sm:inline text-[9px] font-bold border border-white/30 rounded-sm px-1.5 py-0.5 ml-1">⌘K</kbd>
      </button>

      <GlobalSearch />

      <NewLeadWizard open={newLeadOpen} onOpenChange={setNewLeadOpen} />

      {showGetApp && (
        <GetAppOnPhoneModal
          onClose={() => setShowGetApp(false)}
          redirectPath="/"
          subtitle="Scan to open the CRM on your phone"
        />
      )}
    </div>
  );
}
